import { API, graphqlOperation } from 'aws-amplify';
import { listHierarchyDefinitions, listOrgUnits } from 'graphql/queries';
import * as logger from 'utils/logger';

/**
 * Fetches hierarchy definitions for a client
 * @param {string} clientId - The client ID
 * @returns {Promise<Array>} Array of hierarchy level definitions
 */
export const fetchHierarchyDefinitions = async (clientId) => {
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const filter = {
      clientId: { eq: clientId }
    };

    const result = await API.graphql(
      graphqlOperation(listHierarchyDefinitions, {
        filter,
        limit: 100
      })
    );

    const hierarchyDefinitions = result.data.listHierarchyDefinitions?.items || [];
    
    // Find the most recent active hierarchy definition
    // Sort by effectiveDate to get the most recent
    const sortedDefinitions = hierarchyDefinitions
      .filter(def => !def.endDate || new Date(def.endDate) > new Date())
      .sort((a, b) => new Date(b.effectiveDate) - new Date(a.effectiveDate));
    
    const currentDefinition = sortedDefinitions[0];
    
    if (currentDefinition && currentDefinition.levels) {
      // Extract and return the levels array
      const levels = currentDefinition.levels
        .filter(level => level.isUsed !== false)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      
      logger.debug('Fetched hierarchy levels', {
        clientId,
        version: currentDefinition.version,
        levelCount: levels.length,
        levels: levels.map(l => ({ levelId: l.levelId, label: l.label }))
      });
      
      return levels;
    }

    logger.warn('No active hierarchy definition found, using defaults', { clientId });
    // Return default hierarchy if no definition found
    return [
      { levelId: 'ORGLEVEL_1', label: 'District/Region', order: 1, isUsed: true },
      { levelId: 'ORGLEVEL_2', label: 'Area', order: 2, isUsed: true },
      { levelId: 'ORGLEVEL_3', label: 'Division', order: 3, isUsed: true },
      { levelId: 'ORGLEVEL_4', label: 'Organization Level 4', order: 4, isUsed: true },
      { levelId: 'ORGLEVEL_5', label: 'Organization Level 5', order: 5, isUsed: true }
    ];
  } catch (error) {
    logger.error('Failed to fetch hierarchy definitions', { clientId, error });
    // Return default hierarchy if fetch fails
    return [
      { levelId: 'ORGLEVEL_1', label: 'District/Region', order: 1, isUsed: true },
      { levelId: 'ORGLEVEL_2', label: 'Area', order: 2, isUsed: true },
      { levelId: 'ORGLEVEL_3', label: 'Division', order: 3, isUsed: true },
      { levelId: 'ORGLEVEL_4', label: 'Organization Level 4', order: 4, isUsed: true },
      { levelId: 'ORGLEVEL_5', label: 'Organization Level 5', order: 5, isUsed: true }
    ];
  }
};

/**
 * Fetches all org units for a client with their hierarchy levels
 * @param {string} clientId - The client ID
 * @returns {Promise<Object>} Map of orgUnitId to org unit data including orgLevel
 */
export const fetchOrgUnitsWithLevels = async (clientId) => {
  if (!clientId || typeof clientId !== 'string') {
    throw new Error('Valid clientId is required');
  }

  try {
    const filter = {
      clientId: { eq: clientId }
    };

    const result = await API.graphql(
      graphqlOperation(listOrgUnits, {
        filter,
        limit: 1000
      })
    );

    const orgUnits = result.data.listOrgUnits?.items || [];
    
    // Create a map for quick lookup
    const orgUnitMap = {};
    orgUnits.forEach(unit => {
      orgUnitMap[unit.id] = {
        id: unit.id,
        name: unit.name,
        orgLevel: unit.orgLevel || 'ORGLEVEL_1',
        isActive: unit.isActive !== false
      };
    });

    logger.debug('Fetched org units with levels', {
      clientId,
      count: orgUnits.length
    });

    return orgUnitMap;
  } catch (error) {
    logger.error('Failed to fetch org units', { clientId, error });
    return {};
  }
};

/**
 * Gets the hierarchy label for a given org level
 * @param {string} orgLevel - The org level (e.g., 'ORGLEVEL_1')
 * @param {Array} hierarchyDefinitions - Array of hierarchy definitions
 * @returns {string} The label for the org level
 */
export const getHierarchyLabel = (orgLevel, hierarchyDefinitions) => {
  if (!orgLevel || !hierarchyDefinitions) {
    return orgLevel || 'Unknown Level';
  }

  const definition = hierarchyDefinitions.find(h => h.levelId === orgLevel);
  if (definition) {
    return definition.label;
  }

  // Fallback to default labels
  const defaultLabels = {
    'ORGLEVEL_1': 'District/Region',
    'ORGLEVEL_2': 'Area',
    'ORGLEVEL_3': 'Division',
    'ORGLEVEL_4': 'Organization Level 4',
    'ORGLEVEL_5': 'Organization Level 5'
  };

  return defaultLabels[orgLevel] || orgLevel;
};